/**
 * Wallet ConnectButton component
 *
 * Disconnected: shows "Connect Wallet" button that opens login modal
 * Connected: shows truncated checksummed address with dropdown to disconnect
 *
 * Uses useWallet() abstraction — never imports Privy or wagmi directly.
 * Tracks wallet_connected / wallet_disconnected PostHog events.
 */

import { useEffect, useRef, useState } from "react";
import { useWallet } from "../../hooks/use-wallet";
import { trackClientEvent } from "../../lib/analytics";
import { formatAddress } from "../../lib/format";

export function ConnectButton() {
	const { isConnected, address, login, disconnect } = useWallet();
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const prevConnectedRef = useRef(false);

	// Track wallet_connected event when user first connects
	useEffect(() => {
		if (isConnected && address && !prevConnectedRef.current) {
			trackClientEvent("wallet_connected", {
				walletAddress: address.toLowerCase(),
				method: "unknown",
			});
		}
		prevConnectedRef.current = isConnected;
	}, [isConnected, address]);

	// Close dropdown on outside click
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			const target = event.target;
			if (dropdownRef.current && target instanceof Node && !dropdownRef.current.contains(target)) {
				setDropdownOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	function handleDisconnect() {
		trackClientEvent("wallet_disconnected", {});
		disconnect();
		setDropdownOpen(false);
	}

	// Disconnected state
	if (!isConnected || !address) {
		return (
			<button
				type="button"
				onClick={() => login()}
				className="rounded-lg bg-pastel-vibrant-blue px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-colors"
			>
				Connect Wallet
			</button>
		);
	}

	// Connected state
	return (
		<div ref={dropdownRef} className="relative">
			<button
				type="button"
				onClick={() => setDropdownOpen((prev) => !prev)}
				className="rounded-lg border border-pastel-border bg-pastel-white px-4 py-2 text-sm font-medium text-pastel-black hover:bg-pastel-light-gray transition-colors"
			>
				{formatAddress(address)}
			</button>

			{dropdownOpen && (
				<div className="absolute right-0 mt-2 w-48 rounded-md border border-pastel-border bg-pastel-white shadow-pastel-md z-50">
					<button
						type="button"
						onClick={handleDisconnect}
						className="w-full px-4 py-2 text-left text-sm text-pastel-gray hover:bg-pastel-light-gray hover:text-pastel-black rounded-md"
					>
						Disconnect
					</button>
				</div>
			)}
		</div>
	);
}
